import { memo, useState, useEffect, useRef, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format } from 'date-fns';
import { styled } from '@mui/material/styles';
import { analyticsActions } from '@actions';
import { subtractYears } from '@helpers/dates';
import { ProfileName } from '@constants/integrations';
import { GroupByFilter } from '@constants/analytics';
import { useSocialPostsPlatforms, useLocations } from '@hooks';
import { PrintButton } from '@features/Analytics/Common/PrintButton';
import AnalyticsWrapper from '@features/Analytics/AnalyticsWrapper';
import { AnalyticsContainer, HeaderWrapper } from '@features/Analytics/styles';
import FiltersPanel from './FiltersPanel';
import SocialPostsTable from './SocialPostsTable';
import SocialRates from './SocialRates';
import SocialReachTrend from './SocialReachTrend';
import SocialEngagementsTrend from './SocialEngagementsTrend';

const StyledAnalyticsWrapper = styled(AnalyticsWrapper)({
    '@media (max-width: 1024px)': {
        padding: '16px 16px 0',
    },
});

const SocialPosts = memo(() => {
    const firstRender = useRef(true);

    const [filter, setFilter] = useState({
        locations: [],
        platforms: [],
        timeframe: {
            start: subtractYears(new Date(), 1),
            end: new Date(),
        },
        grouping: GroupByFilter.Months,
    });
    const [filterNumber, setFilterNumber] = useState(0);
    const [filterOpened, setFiltersOpened] = useState(false);

    const locations = useSelector(state => state.account?.account?.companies || []);
    const { socialPostsData, socialPostsLoading } = useSelector(state => state.analytics);
    const language = useSelector(state => state.auth.user?.language);
    const dispatch = useDispatch();
    const { mapLocationsToFormValue } = useLocations();
    const { getConnectedPlatforms } = useSocialPostsPlatforms();

    const availableLocations = useMemo(
        () =>
            locations.filter(l =>
                l.profiles?.some(p => p.type === ProfileName.GoogleOfficial || p.type === ProfileName.FacebookOfficial),
            ),
        [locations],
    );
    const availableLocationsIds = useMemo(() => availableLocations.map(l => l.companyId), [availableLocations]);
    const platformsConnected = useMemo(() => getConnectedPlatforms(availableLocationsIds), [availableLocationsIds]);

    useEffect(() => {
        setFilter(prev => ({
            ...prev,
            locations: mapLocationsToFormValue(availableLocations),
            platforms: platformsConnected,
        }));
    }, [availableLocations, platformsConnected, dispatch]);

    useEffect(() => {
        if (filter.locations.length && firstRender.current) {
            onApplyFilters();
            firstRender.current = false;
        }
    }, [filter, dispatch]);

    const onApplyFilters = () => {
        dispatch(
            analyticsActions.getAnalyticsSocialPosts({
                locations: filter.locations.map(l => l.id),
                platforms: filter.platforms,
                from: filter.timeframe.start ? format(new Date(filter.timeframe.start), 'yyyy-MM-dd') : null,
                to: filter.timeframe.end ? format(new Date(filter.timeframe.end), 'yyyy-MM-dd') : null,
                grouping: filter.grouping,
            }),
        );
    };

    return (
        <StyledAnalyticsWrapper>
            <HeaderWrapper>
                <FiltersPanel
                    setFiltersOpened={setFiltersOpened}
                    filtersNumber={filterNumber}
                    filterOpened={filterOpened}
                    filter={filter}
                    setFilter={setFilter}
                    type="social-posts"
                    setFilterNumber={setFilterNumber}
                    onApplyFilters={onApplyFilters}
                />
                <PrintButton />
            </HeaderWrapper>
            <AnalyticsContainer id="pl-content-analytics-social-posts">
                <SocialRates data={socialPostsData.rates} language={language} />
                <SocialReachTrend data={socialPostsData.reachTrendData} language={language} />
                <SocialEngagementsTrend data={socialPostsData.engagementsTrendData} language={language} />
                <SocialPostsTable data={socialPostsData.tableData} isLoading={socialPostsLoading} filter={filter} />
            </AnalyticsContainer>
        </StyledAnalyticsWrapper>
    );
});

SocialPosts.displayName = 'SocialPosts';

export default SocialPosts;
